(() => {
  'use strict';

  const checkoutSelector = '[name="checkout"], #checkout';
  const errorSelector = '[data-cart-validation-error]';
  const summarySelector = '[data-cart-validation-summary]';
  const controllers = new Map();
  let validationInProgress = false;
  let releasedButton = null;
  let releasedForm = null;
  let resetQueue = Promise.resolve();
  let resetStarted = false;
  let needsResponsibilitiesReset = true;

  const messages = {
    contact: 'Please select at least one way we can contact you if an item is unexpectedly unavailable.',
    responsibilities: 'Please read and accept the "Shared Customer Responsibilities" information to complete your checkout.',
    shipping: 'Please choose a shipping date before checkout.',
  };
  const summaryLabels = {
    contact: 'Choose how we may contact you',
    responsibilities: 'Acknowledge the Shared Customer Responsibilities',
    shipping: 'Choose a shipping date',
  };

  function addDescription(control, id) {
    if (!control || !id) return;
    const ids = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    ids.add(id);
    control.setAttribute('aria-describedby', [...ids].join(' '));
  }

  function setError(error, message) {
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  function visibleErrors() {
    const entries = [...document.querySelectorAll(errorSelector)]
      .filter((error) => !error.hidden && error.textContent.trim())
      .map((error) => ({ name: error.dataset.cartValidationRequirement }))
      .filter(({ name }) => summaryLabels[name]);
    return entries.filter(({ name }, index) => entries.findIndex((entry) => entry.name === name) === index);
  }

  function updateSummary() {
    const summary = document.querySelector(summarySelector);
    if (!summary) return;
    const errors = visibleErrors();
    const intro = summary.querySelector('[data-cart-validation-summary-intro]');
    const list = summary.querySelector('[data-cart-validation-summary-list]');

    if (!errors.length) {
      summary.hidden = true;
      if (intro) intro.textContent = '';
      if (list) list.replaceChildren();
      return;
    }

    if (intro) {
      intro.textContent =
        errors.length === 1
          ? 'One required detail still needs your attention. Your cart is saved.'
          : `${errors.length} required details still need your attention. Your cart is saved.`;
    }

    if (list) {
      const items = errors.map(({ name }) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-checkout-validation__link';
        button.dataset.cartValidationJump = name;
        button.textContent = summaryLabels[name];
        item.append(button);
        return item;
      });
      list.replaceChildren(...items);
    }
    summary.hidden = false;
  }

  function makeFallbackController(type) {
    if (type === 'shipping') {
      const visibleInput = document.querySelector('#ShippingDatePicker, #datepicker');
      if (!visibleInput) return null;
      const root =
        visibleInput.closest('[data-shipping-date-calendar], .datepicker-container, [class*="ai-cart-custom-container-"]') ||
        visibleInput.parentElement;
      let error = root?.querySelector('#ShippingDateError, [data-shipping-date-error]');
      if (!error) {
        error = document.createElement('p');
        error.id = 'ShippingDateError';
        error.className = 'shipping-date-calendar__error';
        error.dataset.shippingDateError = '';
        error.dataset.cartValidationError = '';
        error.dataset.cartValidationRequirement = 'shipping';
        error.setAttribute('role', 'alert');
        error.setAttribute('aria-live', 'polite');
        error.hidden = true;
        visibleInput.insertAdjacentElement('afterend', error);
      }
      error.dataset.cartValidationError = '';
      error.dataset.cartValidationRequirement = 'shipping';
      addDescription(visibleInput, error.id);

      return {
        root: () => root,
        focusTarget: () => visibleInput,
        validate: () => {
          const field = document.querySelector('input[name="attributes[Ship date]"]');
          const helpers = window.DancingOaksShippingDates;
          if (!field?.value || !helpers) return false;
          const selected = helpers.parseIsoDate(field.value);
          const pacificNow = helpers.losAngelesNow();
          const maximumDate = helpers.addCalendarMonths(pacificNow.date, 3);
          const blackouts = new Set((root?.dataset.blackoutDates || '').split(',').filter(Boolean));
          return helpers.isSelectableMonday(
            selected,
            pacificNow.date,
            maximumDate,
            blackouts,
            pacificNow.minutes
          );
        },
        showError: (message) => {
          visibleInput.setAttribute('aria-invalid', 'true');
          root?.toggleAttribute('data-invalid', true);
          setError(error, message || messages.shipping);
        },
        clearError: () => {
          visibleInput.setAttribute('aria-invalid', 'false');
          root?.removeAttribute('data-invalid');
          setError(error, '');
        },
      };
    }

    const root = document.querySelector('[data-out-of-stock-contact]');
    if (!root) return null;
    const options = () => [...root.querySelectorAll('[data-out-of-stock-contact-option]')];
    const error = root.querySelector('[data-out-of-stock-contact-error]');
    if (error) {
      error.dataset.cartValidationError = '';
      error.dataset.cartValidationRequirement = 'contact';
    }

    return {
      root: () => root,
      focusTarget: () => options()[0],
      validate: () => options().some((option) => option.checked),
      showError: (message) => {
        options().forEach((option) => option.setAttribute('aria-invalid', 'true'));
        root.toggleAttribute('data-invalid', true);
        setError(error, message || messages.contact);
      },
      clearError: () => {
        options().forEach((option) => option.setAttribute('aria-invalid', 'false'));
        root.removeAttribute('data-invalid');
        setError(error, '');
      },
    };
  }

  function responsibilitiesController() {
    const responsibilityWrappers = [...document.querySelectorAll('.torden-terms-wrapper')];
    const canonicalWrapper = responsibilityWrappers[0];
    responsibilityWrappers.slice(1).forEach((wrapper) => wrapper.remove());

    const checkbox = canonicalWrapper?.querySelector('.torden-terms-checkbox');
    const checkoutArea = document.querySelector('.cart__ctas');
    const root = canonicalWrapper || checkoutArea;
    if (!root) return null;

    if (checkbox && needsResponsibilitiesReset) {
      checkbox.checked = false;
      checkbox.setAttribute('autocomplete', 'off');
      needsResponsibilitiesReset = false;
    }

    let error = document.querySelector('[data-torden-terms-error]');
    if (!error) {
      error = document.createElement('p');
      error.id = 'TordenTermsError';
      error.className = 'cart-responsibilities__error';
      error.dataset.tordenTermsError = '';
      error.dataset.cartValidationError = '';
      error.dataset.cartValidationRequirement = 'responsibilities';
      error.setAttribute('role', 'alert');
      error.setAttribute('aria-live', 'polite');
      error.hidden = true;
      root.append(error);
    } else if (checkbox && error.parentElement !== root) {
      root.append(error);
    }
    error.dataset.cartValidationRequirement = 'responsibilities';

    if (checkbox) addDescription(checkbox, error.id);

    return {
      root: () => checkbox?.closest('.torden-terms-wrapper') || root,
      focusTarget: () => checkbox || document.querySelector('#checkout'),
      validate: () => Boolean(checkbox?.checked),
      showError: () => {
        checkbox?.setAttribute('aria-invalid', 'true');
        root.toggleAttribute('data-invalid', true);
        setError(error, messages.responsibilities);
      },
      clearError: () => {
        checkbox?.setAttribute('aria-invalid', 'false');
        root.removeAttribute('data-invalid');
        setError(error, '');
      },
      reset: () => {
        if (checkbox) checkbox.checked = false;
        checkbox?.setAttribute('aria-invalid', 'false');
        root.removeAttribute('data-invalid');
        setError(error, '');
      },
    };
  }

  function persistEmptyRequirements() {
    resetQueue = resetQueue.catch(() => {}).then(() =>
      fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Agreed to terms on': '',
            'Out-of-stock contact': '',
            'Ship date': '',
          },
        }),
      }).then((response) => {
        if (!response.ok) throw new Error('Unable to reset required cart details');
        return response.json();
      })
    );
    return resetQueue;
  }

  function resetRequiredDetails() {
    needsResponsibilitiesReset = true;
    controllers.forEach((controller) => controller.reset?.());
    responsibilitiesController()?.reset?.();
    updateSummary();
    return persistEmptyRequirements();
  }

  function normalizeResult(result, controller) {
    if (typeof result === 'boolean') return { valid: result };
    if (result && typeof result === 'object') return result;
    return { valid: false, message: controller.message };
  }

  async function validateController(name, controller) {
    try {
      await controller.whenSettled?.();
      const result = normalizeResult(await controller.validate(), controller);
      if (result.valid) {
        controller.clearError?.();
      } else {
        controller.showError?.(result.message || controller.message || messages[name]);
      }
      return { controller, name, valid: result.valid };
    } catch (_error) {
      controller.showError?.(controller.persistenceError || controller.message || messages[name]);
      return { controller, name, valid: false };
    }
  }

  function requirementControllers() {
    const shipping = controllers.get('shipping') || makeFallbackController('shipping');
    const contact = controllers.get('contact') || makeFallbackController('contact');
    return [
      ['shipping', shipping],
      ['contact', contact],
      ['responsibilities', responsibilitiesController()],
    ].filter(([, controller]) => controller);
  }

  const focusMargin = 24;

  function stickyHeaderOffset() {
    return [...document.querySelectorAll('sticky-header, .shopify-section-header-sticky, header')].reduce(
      (offset, element) => {
        const style = getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        if (!['fixed', 'sticky'].includes(style.position) || rect.height <= 0 || rect.bottom <= 0) return offset;
        return Math.max(offset, rect.bottom);
      },
      0
    );
  }

  function nextAnimationFrame() {
    return new Promise((resolve) => window.requestAnimationFrame(resolve));
  }

  function visualViewportBounds() {
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop || 0;
    return {
      bottom: top + (viewport?.height || window.innerHeight),
      top,
    };
  }

  function safeViewportBounds() {
    const viewport = visualViewportBounds();
    return {
      bottom: viewport.bottom - focusMargin,
      top: Math.max(viewport.top, stickyHeaderOffset()) + focusMargin,
    };
  }

  function isControlVisible(target) {
    const rect = target.getBoundingClientRect();
    const bounds = safeViewportBounds();
    return rect.top >= bounds.top - 1 && rect.bottom <= bounds.bottom + 1;
  }

  function targetScrollTop(target) {
    const rect = target.getBoundingClientRect();
    return Math.max(0, window.scrollY + rect.top - safeViewportBounds().top);
  }

  async function waitForViewportToSettle() {
    const viewport = window.visualViewport;
    if (!viewport) {
      await nextAnimationFrame();
      return;
    }

    let previousHeight = viewport.height;
    let previousTop = viewport.offsetTop;
    let stableFrames = 0;

    for (let frame = 0; frame < 12; frame += 1) {
      await nextAnimationFrame();
      const heightIsStable = Math.abs(viewport.height - previousHeight) < 0.5;
      const topIsStable = Math.abs(viewport.offsetTop - previousTop) < 0.5;
      stableFrames = heightIsStable && topIsStable ? stableFrames + 1 : 0;
      previousHeight = viewport.height;
      previousTop = viewport.offsetTop;
      if (frame >= 3 && stableFrames >= 2) return;
    }
  }

  function waitForScrollToSettle(targetY) {
    return new Promise((resolve) => {
      const startedAt = performance.now();
      let previousY = window.scrollY;
      let stableFrames = 0;
      let hasMoved = false;

      function checkScroll(now) {
        const currentY = window.scrollY;
        const movement = Math.abs(currentY - previousY);
        if (movement >= 0.5) hasMoved = true;
        stableFrames = movement < 0.5 ? stableFrames + 1 : 0;
        previousY = currentY;

        const reachedTarget = Math.abs(currentY - targetY) < 1;
        const scrollIsStable = stableFrames >= 3 && (hasMoved || reachedTarget) && now - startedAt >= 100;
        if (scrollIsStable || now - startedAt >= 750) {
          resolve();
          return;
        }

        window.requestAnimationFrame(checkScroll);
      }

      window.requestAnimationFrame(checkScroll);
    });
  }

  async function scrollControlIntoView(target, behavior) {
    const top = targetScrollTop(target);
    if (Math.abs(window.scrollY - top) < 1) return;

    window.scrollTo({ behavior, top });
    if (behavior === 'smooth') {
      await waitForScrollToSettle(top);
    } else {
      await nextAnimationFrame();
    }
  }

  async function focusIssue(issue) {
    const root = issue.controller.root?.();
    const target = issue.controller.focusTarget?.();
    if (!root || !target) return;

    try {
      target.focus({ preventScroll: true });
    } catch (_error) {
      target.focus();
    }

    await waitForViewportToSettle();
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!isControlVisible(target)) {
      await scrollControlIntoView(target, reducedMotion ? 'auto' : 'smooth');
    }

    await waitForViewportToSettle();
    if (!isControlVisible(target)) {
      await scrollControlIntoView(target, 'auto');
    }
  }

  async function validateAll(button, form) {
    if (validationInProgress) return;
    validationInProgress = true;

    try {
      await resetQueue;
    } catch (_error) {
      // The visible controls still start empty, so normal validation remains authoritative.
    }

    const results = await Promise.all(
      requirementControllers().map(([name, controller]) => validateController(name, controller))
    );
    const invalid = results.filter((result) => !result.valid);
    updateSummary();

    if (invalid.length) {
      invalid.sort((a, b) => {
        const aRoot = a.controller.root?.();
        const bRoot = b.controller.root?.();
        if (!aRoot || !bRoot || aRoot === bRoot) return 0;
        return aRoot.compareDocumentPosition(bRoot) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });
      await focusIssue(invalid[0]);
      validationInProgress = false;
      return;
    }

    validationInProgress = false;
    releasedButton = button;
    releasedForm = form;
    button.click();
    window.setTimeout(() => {
      if (releasedButton === button) releasedButton = null;
      if (releasedForm === form) releasedForm = null;
    }, 0);
  }

  function checkoutButtonFromEvent(event) {
    const target = event.target instanceof Element ? event.target.closest(checkoutSelector) : null;
    return target instanceof HTMLButtonElement || target instanceof HTMLInputElement ? target : null;
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = checkoutButtonFromEvent(event);
      if (!button || button.disabled) return;
      if (releasedButton === button) {
        releasedButton = null;
        return;
      }

      const form = button.form || document.querySelector('form#cart');
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      validateAll(button, form);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-cart-validation-jump]');
    if (!button) return;
    const issue = requirementControllers()
      .map(([name, controller]) => ({ controller, name }))
      .find(({ name }) => name === button.dataset.cartValidationJump);
    if (issue) focusIssue(issue);
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart') return;
      if (releasedForm === form) {
        releasedForm = null;
        return;
      }

      const button = event.submitter?.matches?.(checkoutSelector)
        ? event.submitter
        : document.querySelector('#checkout');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      validateAll(button, form);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      if (!event.target.matches?.('.torden-terms-checkbox')) return;
      needsResponsibilitiesReset = false;
      const controller = responsibilitiesController();
      if (event.target.checked) controller?.clearError();
      updateSummary();
    },
    true
  );

  const api = {
    messages,
    notify: updateSummary,
    whenReset: () => resetQueue,
    register(name, controller) {
      controllers.set(name, controller);
      updateSummary();
    },
  };

  window.DancingOaksCartValidation = api;

  function observeResponsibilities() {
    responsibilitiesController();
    new MutationObserver(() => responsibilitiesController()).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    if (resetStarted) return;
    resetStarted = true;
    observeResponsibilities();
    resetRequiredDetails();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetRequiredDetails();
  });
})();
